'use client'

import React from 'react'

import type { LocationsBlock as LocationsBlockProps } from '@/payload-types'

import { SectionWrapper } from '@/components/SectionWrapper'
import SectionHeader from '@/components/ui/SectionHeader'
import { Links } from '@/components/Links/Component'
import { LocationItem } from './LocationItem'
import { normalizeSectionSettings } from '@/utilities/sectionSettings'
import type { ResolvedLocation } from '@/utilities/getCompanyLocations'

type BlockItem = NonNullable<LocationsBlockProps['locations']>[number]

type Props = LocationsBlockProps & {
  resolvedLocations: Array<{ item: BlockItem; data: ResolvedLocation | undefined }>
}

export const LocationsBlockClient: React.FC<Props> = ({
  infoTagText,
  title,
  subtitle,
  links,
  settings,
  resolvedLocations,
}) => {
  const normalizedSettings = normalizeSectionSettings(settings)

  return (
    <>
      {/* Header Section */}
      <SectionWrapper
        settings={normalizedSettings}
        className="overflow-hidden pb-8 lg:pb-16"
      >
        <SectionHeader
          infoTagText={infoTagText || ''}
          title={title}
          subtitle={subtitle || undefined}
        />
        {links && links.length > 0 && (
          <div className="mt-6 md:mt-8">
            <Links
              links={links as any}
              className="flex flex-col sm:flex-row items-start gap-6 md:gap-12"
            />
          </div>
        )}
      </SectionWrapper>
      {/* Location Items */}

      <SectionWrapper
        background="transparent"
        padding="lg"
        fullWidth
        className="overflow-hidden pt-0 lg:pt-0"
      >
        {resolvedLocations.length > 0 && (
          <div className="space-y-20 md:space-y-28">
            {resolvedLocations.map(({ item, data }, index) => {
              // No matching Global entry — silently skip. Editor sees an
              // empty slot in admin; better than rendering broken markup.
              if (!data) return null

              const itemNode = (
                <LocationItem
                  image={item.image}
                  imagePosition={item.imagePosition || undefined}
                  bleed={item.bleed || undefined}
                  infoTagText={item.infoTagText || data.label}
                  title={item.title || data.label}
                  description={item.description as any}
                  links={item.links as any}
                  dark={item.dark || undefined}
                  location={data}
                />
              )

              if (item.dark) {
                return (
                  <div key={index} data-theme="dark" className="bg-[var(--dark)]">
                    {itemNode}
                  </div>
                )
              }

              return <React.Fragment key={index}>{itemNode}</React.Fragment>
            })}
          </div>
        )}
      </SectionWrapper>
    </>
  )
}
